import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StaffTab } from "@/components/console/StaffTab";
import { PartnersTab } from "@/components/console/PartnersTab";
import { ResidentsTab } from "@/components/console/ResidentsTab";
import { AuditTab } from "@/components/console/AuditTab";
import { ContractVendorsTab } from "@/components/console/ContractVendorsTab";
import { RemitToTab } from "@/components/console/RemitToTab";
import { SavingsHeldTab } from "@/components/console/SavingsHeldTab";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";

const TABS = [["staff", "Staff & admins", StaffTab], ["partners", "Partner portal", PartnersTab],
  ["residents", "Resident portal", ResidentsTab], ["vendors", "Contract Vendors", ContractVendorsTab], ["remit", "Invoice remit-to", RemitToTab], ["savings", "Savings held", SavingsHeldTab], ["audit", "Audit log", AuditTab]];

export default function ConsoleAccountsPage() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const sa = user?.role === "super_admin";
  const ct = useCardTabs("staff");
  const DESC = { staff: "TEA staff and admin logins, roles and access", partners: "Partner portal accounts", residents: "Resident portal and PACT Life Pass accounts", vendors: "Employers who hired residents: logins, workers, agreements, hours & invoices", remit: "Legal entity and payment details printed on every invoice PDF", savings: "Resident savings held in TEA-First Reliance ...1403", audit: "Every account change, who made it and when" };
  const Cur = TABS.find(([k]) => k === ct.tab)[2];
  useEffect(() => { if (sa) apiClient.get("/console/meta").then(({ data }) => setMeta(data)).catch((e) => toast.error(formatApiError(e))); }, [sa]);
  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10 text-white">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" data-testid="console-back" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6"><ArrowLeft size={14} /> Back to Console</Link>
        <div className="mono-label mb-2">// SYSTEM CONSOLE · USERS &amp; ROLES</div>
        <h1 className="serif-head text-4xl sm:text-5xl">Accounts</h1>
        <p className="text-nex-chrome mt-2 text-sm md:text-base max-w-2xl">Edit every TEA staff, partner portal and resident portal account. Each change is written to the audit log.</p>
        {!sa ? (
          <div data-testid="console-sa-only" className="mt-10 p-6 border border-white/10 flex items-center gap-3 text-white/70"><Lock size={16} className="text-nex-gold" /> Only a Super Admin can manage accounts.</div>
        ) : meta && ct.compact ? (
          <div ref={ct.ref} className="mt-8">
            {ct.deck ? <CardDeck testid="console-cards" items={TABS.map(([k, l]) => ({ id: k, label: l, desc: DESC[k] }))} onOpen={ct.open} />
              : <><CardsBack testid="console-cards-back" label={TABS.find(([k]) => k === ct.tab)[1]} onClick={ct.close} /><Cur meta={meta} /></>}
          </div>
        ) : meta && (
          <Tabs defaultValue="staff" className="mt-8">
            <TabsList className="bg-white/5 border border-white/10 rounded-sm flex-wrap h-auto">
              {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`console-tab-${k}`} className="font-mono text-[10px] tracking-[0.16em] uppercase data-[state=active]:bg-[#d4a017] data-[state=active]:text-black rounded-sm">{l}</TabsTrigger>)}
            </TabsList>
            {TABS.map(([k, , C]) => <TabsContent key={k} value={k} className="mt-6"><C meta={meta} /></TabsContent>)}
          </Tabs>
        )}
      </div>
    </div>
  );
}
